import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ApiService from "../../api/ApiServices";
import LoadImage from "../../assets/loader.gif";
import prizeGradient from "../../assets/prizeGradient.svg";
import pinkGiftArrow from "../../assets/pink-gift-arrow.png";
import grayGiftArrow from "../../assets/gray-gift-arrow.png";
import { AppPaths } from "../../utils/AppPaths";
import { FontWeights } from "../../utils/font.utils";
import AlertModal from "../AlertModal";

type PointsSummary = {
	lifetimePoints: number;
	availablePoints: number;
};

type PrizeItem = {
	id: string;
	title: string;
	image?: string;
	expiresAt?: string;
	isWelcomePrize?: boolean;
	isClaimed?: boolean;
	isComingSoon?: boolean;
	createdAt?: string;
	updatedAt?: string;
	logo?: string;
	pointsRequired?: number;
	welcomeDurationDays?: number;
	writeup?: string;
	_id?: string;
};

type ClaimItem = {
	id: string;
	pointsAtClaim: number;
	claimedAt?: string;
	prizeTitle: string;
	writeup?: string;
};

type ClaimsPagination = {
	currentPage: number;
	totalPages: number;
	pageSize: number;
	totalItems: number;
};

const getCountdownLabel = (expiresAt?: string): string => {
	if (!expiresAt) {
		return "";
	}

	const diffMs = moment(expiresAt).diff(moment());
	if (diffMs <= 0) {
		return "Claim ended";
	}

	const duration = moment.duration(diffMs);
	const hours = Math.floor(duration.asHours());
	const minutes = duration.minutes();
	const seconds = duration.seconds();

	return `Claim within ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const getPrizeSource = (payload: any): any[] => {
	if (Array.isArray(payload)) {
		return payload;
	}

	if (Array.isArray(payload?.data)) {
		return payload.data;
	}

	return [];
};

const mapPrize = (item: any, index: number): PrizeItem => ({
	id: item?._id,
	title: item?.title,
	writeup: item?.writeup || "",
	pointsRequired: Number(item?.pointsRequired || 0),
	image: item?.image,
	logo: item?.logo,
	expiresAt: item?.expiresAt || item?.expiryDate || item?.endAt || item?.claimBy,
	isWelcomePrize: Boolean(item?.isWelcomePrize),
	isClaimed: Boolean(item?.isClaimed),
	isComingSoon: Boolean(item?.isComingSoon),
});

const getClaimsSource = (payload: any): any[] => {
	if (Array.isArray(payload)) {
		return payload;
	}

	if (Array.isArray(payload?.data)) {
		return payload.data;
	}

	return [];
};

const mapClaim = (item: any, index: number): ClaimItem => ({
	id: String(item?._id || index),
	pointsAtClaim: Number(item?.pointsAtClaim || 0),
	claimedAt: item?.claimedAt || item?.redeemedAt || item?.createdAt,
	prizeTitle: item?.prizeId?.title || item?.title || "Prize",
	writeup: item?.prizeId?.writeup || item?.writeup || "",
});

const Prizes = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState<boolean>(true);
	const [points, setPoints] = useState<PointsSummary>({
		lifetimePoints: 0,
		availablePoints: 0,
	});
	const [prizes, setPrizes] = useState<PrizeItem[]>([]);
	const [claims, setClaims] = useState<ClaimItem[]>([]);
	const [now, setNow] = useState<number>(Date.now());
	const [claimsPagination, setClaimsPagination] = useState<ClaimsPagination>({
		currentPage: 1,
		totalPages: 1,
		pageSize: 10,
		totalItems: 0,
	});
	const [loadingMoreClaims, setLoadingMoreClaims] = useState<boolean>(false);
	const [showAlert, setShowAlert] = useState<boolean>(false);
	const [alertMessage, setAlertMessage] = useState<string>("");

	const openAlert = (message: string) => {
		setAlertMessage(message);
		setShowAlert(true);
	};

	useEffect(() => {
		const timer = setInterval(() => {
			setNow(Date.now());
		}, 1000);

		return () => clearInterval(timer);
	}, []);

	const fetchPrizesPageData = async () => {
		setLoading(true);
		try {
			const [pointsResponse, prizesResponse, claimsResponse] = await Promise.all([
				ApiService.getPoints(),
				ApiService.getPrizes(),
				ApiService.getClaims(1, claimsPagination.pageSize),
			]);

			const pointsData = pointsResponse?.data || {};
			setPoints({
				lifetimePoints: Number(pointsData?.totalPoints || 0),
				availablePoints: Number(pointsData?.availablePoints || 0),
			});

			const prizesData = prizesResponse?.data ?? prizesResponse;
			const prizeSource = getPrizeSource(prizesData);
			setPrizes(prizeSource.map(mapPrize));

			const claimsSource = getClaimsSource(claimsResponse);
			setClaims(claimsSource.map(mapClaim));
			setClaimsPagination({
				currentPage: Number(claimsResponse?.pagination?.currentPage || 1),
				totalPages: Number(claimsResponse?.pagination?.totalPages || 1),
				pageSize: Number(claimsResponse?.pagination?.pageSize || 10),
				totalItems: Number(claimsResponse?.pagination?.totalItems || claimsSource.length || 0),
			});
		} finally {
			setLoading(false);
		}
	};

	const fetchMoreClaims = async () => {
		if (loadingMoreClaims || claimsPagination.currentPage >= claimsPagination.totalPages) {
			return;
		}

		setLoadingMoreClaims(true);
		try {
			const nextPage = claimsPagination.currentPage + 1;
			const claimsResponse = await ApiService.getClaims(nextPage, claimsPagination.pageSize);
			const claimsSource = getClaimsSource(claimsResponse);
			setClaims((prev) => [...prev, ...claimsSource.map(mapClaim)]);
			setClaimsPagination({
				currentPage: Number(claimsResponse?.pagination?.currentPage || nextPage),
				totalPages: Number(claimsResponse?.pagination?.totalPages || claimsPagination.totalPages),
				pageSize: Number(claimsResponse?.pagination?.pageSize || claimsPagination.pageSize),
				totalItems: Number(claimsResponse?.pagination?.totalItems || claimsPagination.totalItems),
			});
		} finally {
			setLoadingMoreClaims(false);
		}
	};

	useEffect(() => {
		fetchPrizesPageData();
	}, []);

	const featuredPrize = useMemo(
		() => prizes.find((prize) => prize.isWelcomePrize && !prize.isClaimed),
		[prizes]
	);
	const regularPrizeGroups = useMemo(() => {
		const grouped = prizes
			.filter((prize) => !prize.isWelcomePrize && !prize.isComingSoon)
			.reduce((acc, prize) => {
				const pointsKey = Number(prize.pointsRequired || 0);
				if (!acc[pointsKey]) {
					acc[pointsKey] = [];
				}
				acc[pointsKey].push(prize);
				return acc;
			}, {} as Record<number, PrizeItem[]>);

		return Object.entries(grouped)
			.map(([pointsValue, groupPrizes]) => ({ pointsValue: Number(pointsValue), groupPrizes }))
			.sort((a, b) => a.pointsValue - b.pointsValue);
	}, [prizes]);
	const comingSoonPrizes = useMemo(
		() => prizes.filter((prize) => !prize.isWelcomePrize && prize.isComingSoon),
		[prizes]
	);
	const comingSoonMinPoints = useMemo(() => {
		if (!comingSoonPrizes.length) {
			return 0;
		}

		return Math.min(...comingSoonPrizes.map((prize) => Number(prize.pointsRequired || 0)));
	}, [comingSoonPrizes]);
	const countdown = useMemo(() => {
		void now;
		return getCountdownLabel(featuredPrize?.expiresAt);
	}, [featuredPrize?.expiresAt, now]);
	const sortedClaims = useMemo(
		() => [...claims].sort((a, b) => moment(b.claimedAt).valueOf() - moment(a.claimedAt).valueOf()),
		[claims]
	);
	const hasMoreClaims = claimsPagination.currentPage < claimsPagination.totalPages;
	const canClaimFeaturedPrize = points.availablePoints >= Number(featuredPrize?.pointsRequired || 0);

	const onLoadMoreClaims = () => {
		if (!hasMoreClaims || loadingMoreClaims) {
			return;
		}

		fetchMoreClaims();
	};

	return (
		<div className="min-h-screen pb-20">
			<div className="flex items-center relative px-3 py-3">
				<img
					onClick={() => navigate(-1)}
					alt="Back"
					className="home-frag-pic-backbtn mr-2 cursor-pointer"
					src={require("../../assets/arrow-left.png")}
				/>
				<div className="btn-onb4-text absolute left-1/2 -translate-x-1/2 text-center">
					Your points!!
				</div>
			</div>

			{loading ? (
				<div className="flex justify-center items-center h-[70vh]">
					<img width="100px" className="center" src={LoadImage} alt="loader" />
				</div>
			) : (
				<div className="px-2 mx-2 pb-20">
					<div className="points-card overflow-hidden">
						<div className="w-full bg-cover bg-center mt-2" style={{ backgroundImage: `url(${prizeGradient})`, height: "55px" }} />
						<div className="flex items-center justify-center mb-1" style={{ marginTop: "-70px" }}>
							<img src={require("../../assets/u4u_black.png")} alt="logo" className="w-16 h-20 object-contain" />
						</div>
						<div className="flex justify-between px-6 pb-2">
							<div className="text-center">
								<div className="inter-24-bold">{points.lifetimePoints.toLocaleString()}</div>
								<div className="inter-regular-14">Lifetime Points</div>
							</div>
							<div className="text-center">
								<div className="inter-24-bold">{points.availablePoints.toLocaleString()}</div>
								<div className="inter-regular-14">Available to spend</div>
							</div>
						</div>
						<div className="pb-3 flex justify-center">
							<div
								onClick={() => navigate(AppPaths.HOME)}
								className="secondary-btn px-6 h-9 inter-14-bold cursor-pointer"
							>
								KEEP SAVING
							</div>
						</div>
					</div>
					{featuredPrize ? (
					<div className="bg-white mt-4 overflow-hidden welcome-prize-card">
						<div className="text-white inter-14-regular relative">
							<img src={require("../../assets/welcome_prize.png")} alt="Special Welcome Prize" className="block mt-2" />
							<span className="absolute left-3 top-1/2 -translate-y-1/2">Special Welcome Prize</span>
						</div>
						
						<div className="px-3 py-3">
							<div className="flex">
								<div className="w-20 h-20 mr-3 shrink-0 flex items-center justify-center">
									<img
										src={featuredPrize.logo || require("../../assets/star.png")}
										alt={featuredPrize.title}
										className="w-full h-full object-contain"
									/>
								</div>
								<div className="flex-1 min-w-0">
									<div className="inter-regular-14">{featuredPrize.title} <span className="inter-regular-14 font-bold">{featuredPrize.writeup}</span></div>
									{countdown ? <div className="inter-regular-12 text-[#EA088C] mt-1">{countdown}</div> : null}
								</div>
							</div>
							<div className="mt-2 justify-center flex">
								<div
									onClick={() => {
										if (!featuredPrize) {
											return;
										}

										if (canClaimFeaturedPrize) {
											navigate(AppPaths.CLAIM_PRIZES, {
												state: {
													prizeId: featuredPrize.id,
													prizeImage: featuredPrize.image || featuredPrize.logo,
													prizeTitle: featuredPrize.title,
													pointsRequired: Number(featuredPrize.pointsRequired || 0),
													writeup: featuredPrize.writeup,
												},
											});
											return;
										}

										openAlert("Keep saving - you’re almost there.");
									}}
									className={`primary-btn welcome-prize-btn ${canClaimFeaturedPrize ? "cursor-pointer opacity-100" : "cursor-not-allowed opacity-60"}`}
								>
									<div className="welcome-prize-btn-text">CLAIM FOR {featuredPrize.pointsRequired} POINTS</div>
								</div>
							</div>
						</div>
						
					</div>
					) : null}

					{regularPrizeGroups.map(({ pointsValue, groupPrizes }) => (
						<div key={pointsValue} className="mt-2">
							<div className="inter-24-bold mb-2">{pointsValue.toLocaleString()}pts Prizes</div>
							<div className="overflow-x-auto">
								<div className="flex gap-3 pb-2 w-max">

									{groupPrizes.map((prize) => {
										const canClaimPrize = points.availablePoints >= Number(prize.pointsRequired || 0);

										return (
											<div
												key={prize.id}
												className="w-[150px] min-h-[194px] gift-card1 p-3 relative"
											>
												<div className="h-20 flex items-center justify-center mb-2">
													<img
														src={prize.logo || prize.image || require("../../assets/star.png")}
														alt={prize.title}
														className="w-16 h-16 object-contain"
													/>
												</div>
												<div className="inter-regular-14 leading-5" style={{ fontWeight: 500 }}>{prize.title}</div>
												<button
													type="button"
													onClick={() => {
														if (canClaimPrize) {
															navigate(AppPaths.CLAIM_PRIZES, {
																state: {
																	prizeId: prize.id,
																	prizeImage: prize.image || prize.logo,
																	prizeTitle: prize.title,
																	pointsRequired: Number(prize.pointsRequired || 0),
																	writeup: prize.writeup,
																},
															});
															return;
														}

														openAlert("Keep saving - you’re almost there.");
													}}
													className={`absolute ${prize.isClaimed ? "opacity-100" : "opacity-100"}`} style={{right:-10, top:150}}
												>
													<img
														src={canClaimPrize ? pinkGiftArrow : grayGiftArrow}
														alt={canClaimPrize ? "Claim prize" : "Not enough points"}
														className=""
													/>
												</button>
											</div>
										);
									})}
								</div>
							</div>
						</div>
					))}

					{comingSoonPrizes.length ? (
						<div className="mt-2">
							<div className="inter-24-bold mb-2">
								Coming Soon! {comingSoonMinPoints.toLocaleString()}pts+ Prizes
							</div>
							<div className="overflow-x-auto">
								<div className="flex gap-3 pb-2 w-max">
									{comingSoonPrizes.map((prize) => (
										<div
											key={prize.id}
											className="w-[138px] min-h-[172px] gift-card p-3"
										>
											<div className="h-20 flex items-center justify-center mb-2">
												<img
													src={prize.logo || prize.image || require("../../assets/star.png")}
													alt={prize.title}
													className="w-16 h-16 object-contain"
												/>
											</div>
											<div className="inter-regular-14 text-gray-500 leading-5">{prize.title}</div>
										</div>
									))}
								</div>
							</div>
						</div>
					) : null}

					{sortedClaims.length ? (
						<div className="bg-white py-2">
							<div className="inter-regular-14 font-semibold">Past Cash-ins</div>
							{sortedClaims.map((claim, index) => (
								<div key={claim.id} className="pt-2">
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<div className="inter-regular-14 truncate">{claim.prizeTitle}</div>
											<div className="inter-regular-14 text-[#667085]">{moment(claim.claimedAt).format("DD MMM, YYYY")}</div>
										</div>
										<div className="inter-regular-14 text-[#F73294]">-{claim.pointsAtClaim.toLocaleString()}pts</div>
									</div>
									{index < sortedClaims.length - 1 ? <div className="mt-2 border-b border-[#E4E7EC]" /> : null}
								</div>
							))}
							{hasMoreClaims ? (
								<div className="flex justify-center mt-3">
									<div
										onClick={onLoadMoreClaims}
										className="btn-onb4-text underline underline-offset-4 cursor-pointer"
									>
										{loadingMoreClaims ? "Loading..." : "Load More"}
									</div>
								</div>
							) : null}
						</div>
					) : null}
				</div>
			)}
			<AlertModal
				visible={showAlert}
				title="Not Enough Points!"
				message={alertMessage}
				onConfirm={() => setShowAlert(false)}
			/>
		</div>
	);
};

export default Prizes;
